import { useApiData } from 'lib/api';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  LoadingIndicator,
  MoreOptions,
  PublishedProductText
} from 'components/common';
import { BoxTableCell, BoxTableRow } from 'components/common/box';
import { ParticipantsStats } from 'components/common/stats';
import {
  CourseTitleWithInfo,
  GroupCourseTargetSchedule
} from 'components/courses/common';
import { ShareGroupsTargetSchedule } from 'components/courses/share';
import { GroupCoursesRemove } from '.';

import type { Course } from 'shared-types';
import type { GetGroupCoursesRes } from 'shared-types/responses';

interface Props {
  course: Pick<
    Course,
    | 'completionEnabled'
    | 'deadlineDate'
    | 'deadlineDurationUnit'
    | 'deadlineDurationValue'
    | 'deadlineType'
    | 'endDate'
    | 'id'
    | 'picture'
    | 'picturePosition'
    | 'slug'
    | 'startDate'
    | 'title'
  > &
    Pick<
      GetGroupCoursesRes['courses'][number],
      | 'canEditSettings'
      | 'groupDeadlineDate'
      | 'groupDeadlineDurationUnit'
      | 'groupDeadlineDurationValue'
      | 'groupDeadlineType'
      | 'isCourseMandatory'
      | 'isMandatory'
    >;
  updateCourses: () => Promise<void>;
}

const GroupCoursesCourse = ({ course, updateCourses }: Props) => {
  const {
    messages,
    pageState: { group },
    theme
  } = useReduxState();

  const { data, loading } = useApiData<GetGroupCoursesRes>({
    url: `/admin/groups/${group.id}/courses?courseId=${course.id}&includeProgressData=true`
  });

  const progressData = data?.courses?.[0]?.progressData;

  return (
    <BoxTableRow>
      <BoxTableCell
        cellBreakpoint={breakpoints.medium}
        label={messages.commonCourse}
      >
        <CourseTitleWithInfo
          course={course}
          linkToCourseAdminPage={true}
          titleStyle={{
            color: course.startDate ? theme.textColor : theme.textColorLighter
          }}
        >
          <PublishedProductText
            endDate={course.endDate as string}
            startDate={course.startDate as string}
          />
        </CourseTitleWithInfo>
      </BoxTableCell>

      <BoxTableCell
        cellBreakpoint={breakpoints.medium}
        label={messages.commonMandatory}
      >
        {course.isCourseMandatory || course.isMandatory
          ? messages.commonMandatory
          : null}
      </BoxTableCell>

      <BoxTableCell
        cellBreakpoint={breakpoints.medium}
        label={messages.courseTargetSchedule}
      >
        <GroupCourseTargetSchedule
          targetSchedule={{
            deadlineDate: course.groupDeadlineDate,
            deadlineDurationUnit: course.groupDeadlineDurationUnit,
            deadlineDurationValue: course.groupDeadlineDurationValue,
            deadlineType: course.groupDeadlineType
          }}
        />
      </BoxTableCell>

      <BoxTableCell
        cellBreakpoint={breakpoints.medium}
        label={messages.commonProgress}
      >
        {loading ? (
          <LoadingIndicator padding={5} size={20} />
        ) : (
          <ParticipantsStats
            completionEnabled={course.completionEnabled}
            progressData={progressData}
          />
        )}
      </BoxTableCell>

      <BoxTableCell shrink={true} verticalBreakpoint={breakpoints.medium}>
        <MoreOptions styleContentButtons={false}>
          {course.canEditSettings ? (
            <ShareGroupsTargetSchedule
              course={course}
              group={{
                ...group,
                deadlineDate: course.groupDeadlineDate,
                deadlineDurationUnit: course.groupDeadlineDurationUnit,
                deadlineDurationValue: course.groupDeadlineDurationValue,
                deadlineType: course.groupDeadlineType,
                isMandatory: course.isMandatory
              }}
              isCourseMandatory={course.isCourseMandatory}
              refresh={updateCourses}
            />
          ) : null}

          <GroupCoursesRemove
            courseId={course.id}
            updateCourses={updateCourses}
          />
        </MoreOptions>
      </BoxTableCell>
    </BoxTableRow>
  );
};

export default GroupCoursesCourse;
